import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { ClientDto, GoodsStatus, ProjectDto } from '../../api/ClientApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers';
import { Box, Grid2, Typography } from '@mui/material';
import { formatNumber, getAddressFromLoactionInfo } from '../../common/Extensions';
import { WarehouseClient } from '../../api/Extensions';
import { useAppSelector } from '../../hooks/hooks';
import { isEmployeeFinanceManager } from '../../redux/selectors/employeeSelector';

type ProjectInfoProps = {
    project?: ProjectDto;
};

export default function ProjectInfo(props: ProjectInfoProps): ReactElement {
    const { project } = props;

    const clients = useAppSelector((state: RootState) => state.clients.clients);

    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);

    const goods = useAppSelector((state: RootState) => state.goods.goods);

    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);

    const currGoods = useMemo(() => {
        return goods?.filter((g) => g.site?.projectId === project?.id);
    }, [goods, project]);

    const totalValueOnProject = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.InStock)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.batch?.purchasePricePerArticle ?? 0));
            }, 0);
    }, [currGoods]);

    const totalValueUsed = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.Used)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.batch?.purchasePricePerArticle ?? 0));
            }, 0);
    }, [currGoods]);

    const totalValueWrittenOff = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.WrittenOff)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.batch?.purchasePricePerArticle ?? 0));
            }, 0);
    }, [currGoods]);

    const totalValueSold = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.Sold)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.exitInfo?.sellingPrice ?? 0));
            }, 0);
    }, [currGoods]);

    return (
        <Box sx={{ p: 0, pl: { xs: 1, sm: 3 }, pt: 2, pr: { xs: 0, sm: 2 }, m: 0, textAlign: 'start' }}>
            <Grid2 container spacing={0}>
                <Grid2 size={{ xs: 12, md: 8, xl: 9 }}>
                    <Typography variant="body1" className="info-text">
                        Naziv projekta: <span style={{ fontWeight: 'bold' }}>{project?.name}</span>
                    </Typography>
                    <Typography variant="body1" className="info-text">
                        Klijent:{' '}
                        <span style={{ fontWeight: 'bold' }}>
                            {project?.client?.shortName ?? project?.client?.name ?? '/'}
                        </span>
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4, xl: 3 }}>
                    {isFinanceManager && (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" className="info-text">
                                    Vrednost na projektu:{' '}
                                </Typography>
                                <span style={{ fontWeight: 'bold' }}>
                                    {totalValueOnProject ? formatNumber(totalValueOnProject) : '/'}
                                </span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" className="info-text">
                                    Vrednost ugrađene robe:{' '}
                                </Typography>
                                <span style={{ fontWeight: 'bold' }}>
                                    {totalValueUsed ? formatNumber(totalValueUsed) : '/'}
                                </span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" className="info-text">
                                    Vrednost otpisane robe:{' '}
                                </Typography>
                                <span style={{ fontWeight: 'bold' }}>
                                    {totalValueWrittenOff ? formatNumber(totalValueWrittenOff) : '/'}
                                </span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" className="info-text">
                                    Vrednost prodate robe:{' '}
                                </Typography>
                                <span style={{ fontWeight: 'bold' }}>
                                    {totalValueSold ? formatNumber(totalValueSold) : '/'}
                                </span>
                            </Box>
                        </>
                    )}
                </Grid2>
            </Grid2>
        </Box>
    );
}
